import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  alpha,
  LinearProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Assignment as BorrowIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Inventory as InventoryIcon,
  Numbers as NumbersIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Description as DescriptionIcon,
  Circle as CircleIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { canManageInventory } from '../../../utils/inventoryPermissions';
import dayjs from 'dayjs';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import { injectDetailsPageStyles } from '../../common/DetailsPagePrimitives';
import SystemInformationSection from '../../common/SystemInformationSection';

// ── Design tokens ─────────────────────────────────────────────────────────────
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_MID = '#0a1628';
const SURFACE_UP  = '#0f1f38';
const SURFACE_TOP = '#152640';

const TEXT_BODY  = '#E2E8F0';
const TEXT_MUTED = '#64748B';

// ── MetricCard ────────────────────────────────────────────────────────────────
const MetricCard = ({ label, value, icon: Icon, color = NEON, sub, subColor }) => (
  <Box sx={{
    position: 'relative',
    borderRadius: '14px', px: 2.5, py: 2,
    background: `linear-gradient(145deg, ${alpha(color, 0.08)} 0%, ${alpha(SURFACE_UP, 0.55)} 100%)`,
    outline: `1px solid ${alpha(color, 0.18)}`,
    boxShadow: `inset 0 1px 0 ${alpha(color, 0.08)}`,
    overflow: 'hidden',
  }}>
    <Box sx={{
      position: 'absolute', top: -18, right: -18,
      width: 66, height: 66, borderRadius: '50%',
      background: alpha(color, 0.08), filter: 'blur(18px)', pointerEvents: 'none',
    }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.9 }}>
      {Icon && <Icon sx={{ fontSize: 12, color: alpha(color, 0.7) }} />}
      <Typography sx={{
        fontSize: '0.62rem', fontFamily: "'DM Sans', sans-serif",
        color: TEXT_MUTED, letterSpacing: '0.09em',
        textTransform: 'uppercase', fontWeight: 600,
      }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{
      fontFamily: "'Rajdhani', sans-serif", fontWeight: 700,
      fontSize: '1.45rem', color, lineHeight: 1.1, letterSpacing: 0.3,
    }}>
      {value ?? '—'}
    </Typography>
    {sub && (
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif", fontSize: '0.68rem',
        color: subColor || TEXT_MUTED, mt: 0.4,
      }}>
        {sub}
      </Typography>
    )}
  </Box>
);

// ── Borrow Period Timeline ────────────────────────────────────────────────────
const BorrowPeriodTimeline = ({ startDate, endDate, daysRemaining, daysOverdue }) => {
  if (!startDate || !endDate) return null;
  const start = dayjs(startDate);
  const end   = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return null;

  const totalDays   = Math.max(end.diff(start, 'day'), 1);
  const elapsedDays = daysRemaining > 0 ? Math.max(totalDays - daysRemaining, 0) : totalDays;
  const progressPct = Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100));
  const isExpired   = daysRemaining <= 0;
  const barColor    = isExpired ? '#F87171' : NEON;

  return (
    <Box sx={{ mt: 2, pt: 1.8, borderTop: `1px solid ${alpha(NEON, 0.1)}` }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem',
          color: TEXT_MUTED, fontWeight: 500,
        }}>
          Borrow Period
        </Typography>
        <Typography sx={{
          fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem',
          color: isExpired ? '#F87171' : alpha(NEON, 0.65),
        }}>
          {isExpired
            ? (daysOverdue > 0 ? `${daysOverdue}d overdue` : 'Completed')
            : `${Math.round(progressPct)}% elapsed`}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progressPct}
        sx={{
          height: 9, borderRadius: 5,
          background: alpha(SURFACE_UP, 0.7),
          '& .MuiLinearProgress-bar': {
            background: isExpired
              ? '#F87171'
              : `linear-gradient(90deg, ${NEON2}, ${barColor})`,
            boxShadow: isExpired ? 'none' : `0 0 10px ${alpha(barColor, 0.5)}`,
            borderRadius: 5,
          },
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
        <Typography sx={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '0.62rem', color: alpha(TEXT_MUTED, 0.65),
        }}>
          {start.format('D MMM YYYY')}
        </Typography>
        <Typography sx={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '0.62rem',
          color: isExpired ? alpha('#F87171', 0.65) : alpha(TEXT_MUTED, 0.65),
        }}>
          {end.format('D MMM YYYY')}
        </Typography>
      </Box>
    </Box>
  );
};

// ── Section ───────────────────────────────────────────────────────────────────
const Section = ({ title, icon: Icon, accent = NEON, delay = 0, bodyFlex = false, children }) => (
  <Box sx={{
    borderRadius: '14px',
    border: `1px solid ${alpha(accent, 0.14)}`,
    background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.75)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animation: `fadeUp 0.4s ease ${delay}s both`,
    '@keyframes fadeUp': {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to:   { opacity: 1, transform: 'translateY(0)' },
    },
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.2,
      px: 2.5, py: 1.5, flexShrink: 0,
      borderBottom: `1px solid ${alpha(accent, 0.08)}`,
      background: alpha(accent, 0.03),
    }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '7px',
        background: alpha(accent, 0.12),
        border: `1px solid ${alpha(accent, 0.22)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 13, color: accent }} />
      </Box>
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
        fontSize: '0.72rem', color: alpha(TEXT_BODY, 0.85),
        letterSpacing: '0.08em', textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2, ...(bodyFlex && { flex: 1, display: 'flex', flexDirection: 'column' }) }}>
      {children}
    </Box>
  </Box>
);

// ── InfoRow ───────────────────────────────────────────────────────────────────
const InfoRow = ({ icon: Icon, label, value, color = NEON, emptyLabel = 'Not specified', onClick }) => {
  const isEmpty     = !value;
  const interactive = Boolean(onClick) && !isEmpty;
  return (
    <Box
      component={interactive ? 'button' : 'div'}
      type={interactive ? 'button' : undefined}
      onClick={interactive ? onClick : undefined}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        py: 0.85, px: 1, borderRadius: '9px',
        width: interactive ? '100%' : undefined,
        textAlign: 'left', cursor: interactive ? 'pointer' : 'default',
        border: 'none', background: 'transparent',
        transition: 'background 0.15s ease',
        ...(interactive && { '&:hover': { background: alpha(color, 0.07) } }),
      }}
    >
      <Box sx={{
        width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
        background: alpha(color, isEmpty ? 0.05 : 0.1),
        border: `1px solid ${alpha(color, isEmpty ? 0.1 : 0.18)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 14, color: isEmpty ? TEXT_MUTED : alpha(color, 0.85) }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.62rem', color: TEXT_MUTED,
          fontFamily: "'DM Sans', sans-serif",
          textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600,
        }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: '0.86rem',
          color: isEmpty ? TEXT_MUTED : interactive ? alpha(color, 0.9) : TEXT_BODY,
          fontStyle: isEmpty ? 'italic' : 'normal',
          fontFamily: "'DM Sans', sans-serif",
          fontWeight: isEmpty ? 400 : 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          textDecoration: interactive ? 'underline' : 'none',
          textUnderlineOffset: 3,
        }}>
          {value || emptyLabel}
        </Typography>
      </Box>
      {interactive && (
        <OpenInNewIcon sx={{ fontSize: 13, color: alpha(color, 0.45), flexShrink: 0 }} />
      )}
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const BorrowDetails = () => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [borrow,          setBorrow]          = useState(null);
  const [stock,           setStock]           = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [successMessage,  setSuccessMessage]  = useState(null);
  const [deleteDialogOpen,setDeleteDialogOpen]= useState(false);

  const canManageBorrow = canManageInventory(currentUser);
  const formatAuditUser = (u) => (u?.name ? String(u.name) : null);

  useEffect(() => { injectDetailsPageStyles(); }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    if (!canManageBorrow) { navigate('/dashboard'); return; }
    fetchBorrow();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, id, canManageBorrow, navigate]);

  const fetchBorrow = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/borrows/${id}`);
      setBorrow(res.data);
      if (res.data.stock_id) {
        const stockRes = await api.get(`/stocks/${res.data.stock_id}`);
        setStock(stockRes.data);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching borrow:', err);
      setError('Failed to load borrow details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBorrow = async () => {
    try {
      const borrowerName = borrow?.borrower_name || 'Borrow Record';
      const response = await api.post(`/borrows/${id}/archive`);
      setBorrow(response.data);
      setDeleteDialogOpen(false);
      setSuccessMessage(`Borrow record for "${borrowerName}" has been archived.`);
    } catch (err) {
      console.error('Error deleting borrow:', err);
      setError(err.response?.data?.message || 'Failed to archive borrow record. Please try again.');
      setDeleteDialogOpen(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':  return '#FCD34D';
      case 'approved': return NEON;
      case 'returned': return NEON3;
      case 'overdue':  return '#F87171';
      case 'archived': return '#94A3B8';
      default:         return NEON;
    }
  };

  // ── Guard renders ─────────────────────────────────────────────────────────
  if (!currentUser)    return <Container maxWidth="lg" sx={{ mt: 4 }}><FeedbackMessage message="Please log in to view this page." severity="error" /></Container>;
  if (!canManageBorrow)return <Container maxWidth="lg" sx={{ mt: 4 }}><FeedbackMessage message="You don't have permission to view this page." severity="error" /></Container>;
  if (loading)         return <Loading message="Loading borrow details" size={56} variant="primary" />;
  if (error && !borrow)return <Container maxWidth="lg" sx={{ mt: 4 }}><FeedbackMessage message={error} severity="error" /></Container>;
  if (!borrow)         return <Container maxWidth="lg" sx={{ mt: 4 }}><FeedbackMessage message="Borrow record not found" severity="error" /></Container>;

  // ── Derived values ────────────────────────────────────────────────────────
  const isOverdue    = !borrow.archived_at && borrow.status !== 'returned' && dayjs(borrow.penalty_date).isBefore(dayjs(), 'day');
  const daysOverdue  = isOverdue ? dayjs().diff(dayjs(borrow.penalty_date), 'day') : 0;
  const daysUntilDue = !borrow.archived_at && borrow.status !== 'returned'
    ? dayjs(borrow.penalty_date).diff(dayjs(), 'day')
    : null;
  const actualStatus = borrow.archived_at ? 'archived' : (isOverdue ? 'overdue' : borrow.status);
  const statusColor  = getStatusColor(actualStatus);
  const isUnreturnedActiveBorrow = !borrow.archived_at && borrow.status?.toLowerCase() !== 'returned';
  const dueDateColor = isOverdue
    ? '#F87171'
    : daysUntilDue !== null && daysUntilDue <= 3
      ? '#FCD34D'
      : NEON3;

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

      {/* ── Page Header ── */}
      <Header
        title="Borrow Details"
        subtitle="View borrow information"
        icon={BorrowIcon}
        showBackButton
        onBackClick={() => navigate(-1)}
        actionButton={
          canManageBorrow && (
            <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
              <Button
                startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                onClick={() => navigate(`/borrows/edit/${id}`)}
                sx={{
                  borderRadius: '10px', px: 2, py: 0.65,
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.82rem',
                  background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.55)}`,
                  color: NEON, transition: 'all 0.18s ease',
                  '&:hover': { background: alpha(NEON, 0.07), borderColor: NEON, boxShadow: `0 0 12px ${alpha(NEON, 0.18)}` },
                }}
              >
                Edit
              </Button>
              {!borrow.archived_at && <Button
                startIcon={<ArchiveIcon sx={{ fontSize: 15 }} />}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  borderRadius: '10px', px: 2, py: 0.65,
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.82rem',
                  background: 'transparent', border: `1.5px solid ${alpha('#F87171', 0.55)}`,
                  color: '#F87171', transition: 'all 0.18s ease',
                  '&:hover': { background: alpha('#F87171', 0.08), borderColor: '#F87171', boxShadow: `0 0 12px ${alpha('#F87171', 0.18)}` },
                }}
              >
                Archive
              </Button>}
            </Box>
          )
        }
      />

      <FeedbackMessage message={error}          severity="error"   onClose={() => setError(null)}          sx={{ mb: 2 }} />
      <FeedbackMessage message={successMessage} severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }} />

      {/* ── Overdue Alert ── */}
      {isOverdue && (
        <Box sx={{
          p: 1.6, mb: 2, borderRadius: '10px',
          border: `1px solid ${alpha('#F87171', 0.3)}`,
          background: alpha('#F87171', 0.07),
          display: 'flex', alignItems: 'center', gap: 1.25,
          animation: 'fadeUp 0.3s ease both',
          '@keyframes fadeUp': {
            from: { opacity: 0, transform: 'translateY(8px)' },
            to:   { opacity: 1, transform: 'translateY(0)' },
          },
        }}>
          <WarningIcon sx={{ color: '#F87171', fontSize: 17, flexShrink: 0 }} />
          <Typography sx={{
            fontFamily: "'DM Sans', sans-serif", fontSize: '0.86rem',
            color: '#fca5a5', fontWeight: 500, lineHeight: 1.5,
          }}>
            {daysOverdue} day{daysOverdue !== 1 ? 's' : ''} overdue — due{' '}
            <strong>{dayjs(borrow.penalty_date).format('MMMM D, YYYY')}</strong>.
            {' '}Follow up with <strong>{borrow.borrower_name}</strong> for immediate return.
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* ── Hero Card ── */}
        <Box sx={{
          borderRadius: '16px',
          border: `1px solid ${alpha(NEON, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.9)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
          p: { xs: 2, md: 2.5 },
          animation: 'fadeUp 0.35s ease both',
          '@keyframes fadeUp': {
            from: { opacity: 0, transform: 'translateY(10px)' },
            to:   { opacity: 1, transform: 'translateY(0)' },
          },
        }}>
          {/* Borrower identity + status */}
          <Box sx={{
            display: 'flex', alignItems: 'flex-start',
            justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 50, height: 50, borderRadius: '13px', flexShrink: 0,
                background: `linear-gradient(135deg, ${alpha(NEON2, 0.22)}, ${alpha(NEON, 0.12)})`,
                border: `1.5px solid ${alpha(NEON2, 0.32)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <PersonIcon sx={{ fontSize: 22, color: NEON2 }} />
              </Box>
              <Box>
                <Typography sx={{
                  fontSize: '1.4rem', fontWeight: 700,
                  fontFamily: "'Rajdhani', sans-serif",
                  color: TEXT_BODY, letterSpacing: 0.3, lineHeight: 1.15,
                }}>
                  {borrow.borrower_name || 'Unknown Borrower'}
                </Typography>
                {/* Contact + item link — only show what exists */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
                  {borrow.borrower_contact && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <PhoneIcon sx={{ fontSize: 11, color: alpha(NEON2, 0.7) }} />
                      <Typography sx={{
                        fontSize: '0.78rem', color: NEON2,
                        fontFamily: "'JetBrains Mono', monospace", letterSpacing: 0.3,
                      }}>
                        {borrow.borrower_contact}
                      </Typography>
                    </Box>
                  )}
                  {borrow.borrower_contact && stock?.things && (
                    <Typography sx={{ fontSize: '0.7rem', color: TEXT_MUTED }}>·</Typography>
                  )}
                  {stock?.things && (
                    <Typography
                      component="button"
                      type="button"
                      onClick={() => navigate(`/stocks/${stock.id}`)}
                      sx={{
                        fontSize: '0.78rem', color: alpha(NEON2, 0.8),
                        fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
                        cursor: 'pointer', background: 'none', border: 'none', p: 0,
                        textDecoration: 'underline', textUnderlineOffset: 3,
                        '&:hover': { color: NEON },
                      }}
                    >
                      {stock.things}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Status pill */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.7,
              px: 1.4, py: 0.5, borderRadius: '20px',
              background: alpha(statusColor, 0.1),
              border: `1px solid ${alpha(statusColor, 0.3)}`,
              alignSelf: 'flex-start',
            }}>
              <CircleIcon sx={{ fontSize: 7, color: statusColor }} />
              <Typography sx={{
                fontSize: '0.73rem', fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700, color: statusColor, letterSpacing: '0.05em',
              }}>
                {actualStatus.charAt(0).toUpperCase() + actualStatus.slice(1)}
              </Typography>
            </Box>
          </Box>

          {/* ── Metric Cards ── */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 1.5,
          }}>
            <MetricCard
              label="Quantity Borrowed"
              value={`${borrow.quantity} unit${borrow.quantity !== 1 ? 's' : ''}`}
              icon={NumbersIcon}
              color={NEON}
            />
            <MetricCard
              label="Penalty Fee"
              value={borrow.penalty > 0 ? `RM ${Number(borrow.penalty).toLocaleString()}` : 'None'}
              icon={MoneyIcon}
              color={borrow.penalty > 0 ? '#F87171' : TEXT_MUTED}
            />
            <MetricCard
              label="Due Date"
              value={dayjs(borrow.penalty_date).format('D MMM YYYY')}
              icon={CalendarIcon}
              color={dueDateColor}
              sub={isOverdue
                ? `${daysOverdue}d overdue`
                : daysUntilDue !== null
                  ? `${Math.max(daysUntilDue, 0)}d remaining`
                  : 'Returned'}
              subColor={isOverdue ? '#F87171' : daysUntilDue !== null && daysUntilDue <= 3 ? '#FCD34D' : NEON3}
            />
          </Box>

          {/* ── Timeline ── */}
          <BorrowPeriodTimeline
            startDate={borrow.created_at}
            endDate={borrow.penalty_date}
            daysRemaining={Math.max(daysUntilDue ?? 0, 0)}
            daysOverdue={daysOverdue}
          />
        </Box>

        {/* ── Two-column: Borrow Info + Borrowed Item ── */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          alignItems: 'stretch',
        }}>
          {/* Left: Borrow Info */}
          <Section title="Borrow Info" icon={BorrowIcon} accent={NEON} delay={0.06} bodyFlex>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <InfoRow icon={PersonIcon}  label="Borrower"        value={borrow.borrower_name}    color={NEON} />
              {borrow.borrower_contact && (
                <InfoRow icon={PhoneIcon} label="Contact"         value={borrow.borrower_contact} color={NEON} />
              )}
              {borrow.return_date && (
                <InfoRow icon={ScheduleIcon} label="Returned On"  value={dayjs(borrow.return_date).format('D MMM YYYY, h:mm a')} color={NEON3} />
              )}
            </Box>

            {/* Borrow remarks — inside Borrow Info card */}
            {borrow.remark && (
              <>
                <Box sx={{ my: 1.5, borderTop: `1px solid ${alpha(NEON, 0.08)}` }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{
                    width: 26, height: 26, borderRadius: '7px', flexShrink: 0,
                    background: alpha(NEON, 0.1), border: `1px solid ${alpha(NEON, 0.2)}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <DescriptionIcon sx={{ fontSize: 13, color: alpha(NEON, 0.8) }} />
                  </Box>
                  <Typography sx={{
                    fontFamily: "'DM Sans', sans-serif", fontSize: '0.62rem',
                    color: TEXT_MUTED, textTransform: 'uppercase',
                    letterSpacing: '0.07em', fontWeight: 600,
                  }}>
                    Remarks
                  </Typography>
                </Box>
                <Box sx={{
                  flex: 1, px: 1.75, py: 1.5,
                  borderRadius: '0 9px 9px 0',
                  borderLeft: `3px solid ${alpha(NEON, 0.35)}`,
                  background: alpha(SURFACE_TOP, 0.4),
                }}>
                  <Typography sx={{
                    fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem',
                    color: TEXT_BODY, lineHeight: 1.75,
                  }}>
                    {borrow.remark}
                  </Typography>
                </Box>
              </>
            )}
          </Section>

          {/* Right: Borrowed Item */}
          <Section title="Borrowed Item" icon={InventoryIcon} accent={NEON2} delay={0.09} bodyFlex>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <InfoRow
                icon={InventoryIcon}
                label="Item Name"
                value={stock?.things}
                color={NEON2}
                onClick={stock ? () => navigate(`/stocks/${stock.id}`) : undefined}
              />
              <InfoRow
                icon={LocationIcon}
                label="Storage Location"
                value={stock?.place}
                color={NEON2}
              />
              <InfoRow
                icon={NumbersIcon}
                label="Total Inventory"
                value={stock?.quantity != null ? `${stock.quantity} units` : null}
                color={NEON3}
              />
            </Box>
          </Section>
        </Box>

        {/* ── System Information ── */}
        <SystemInformationSection
          createdBy={formatAuditUser(borrow.record_creator)}
          createdAt={dayjs(borrow.created_at).format('DD MMM YYYY, h:mm a')}
          updatedBy={formatAuditUser(borrow.last_updater)}
          updatedAt={dayjs(borrow.updated_at).format('DD MMM YYYY, h:mm a')}
          delay={0.15}
          accentColor={NEON}
          surfaceUp={SURFACE_UP}
          surfaceMid={SURFACE_MID}
          textBody={TEXT_BODY}
          textMuted={TEXT_MUTED}
        />
      </Box>

      {/* ── Delete Confirmation ── */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteBorrow}
        itemType="borrow record"
        itemName={borrow?.borrower_name}
        title="Archive Borrow"
        description={
          isUnreturnedActiveBorrow
            ? 'This borrow record has not been returned yet. Archiving it will remove its quantity from active inventory calculations, so the stock becomes available for new borrow records. Are you sure you want to archive it?'
            : 'The borrow record will be archived and remain in the system for history and audit purposes.'
        }
        confirmLabel="Archive Borrow"
        actionVerb="archive"
      />
    </Container>
  );
};

export default BorrowDetails;
